import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Door, Tier } from "@/features/funnel/types";

const STORAGE_KEY = "secret-missions-funnel";

/**
 * The in-flight funnel run. This is the client half of the state split:
 * everything here is what the visitor has picked so far — server truth
 * (referrer name, handler roster, SMS number) lives in TanStack Query.
 *
 * Persisted via zustand/persist so a hard refresh resumes mid-funnel (the
 * playbook's return-visitor logic depends on knowing the exact
 * abandonment spot). The persist envelope carries a schema `version`;
 * bump it (with a `migrate` case) whenever a field is renamed or its
 * meaning changes.
 */
export interface FunnelState {
  /** Answered the intro feeling question with "yes". */
  handlerPromotionComplete: boolean;
  feelingAnswered: boolean;
  /** Clicked "I'm in." on the order screen. */
  orderAccepted: boolean;
  door: Door | null;
  /** Operation code generated when a door is picked (e.g. "RedLantern7"). */
  opCode: string | null;
  /** Chosen handler name (e.g. "Veronica"). */
  handler: string | null;
  /** Per-run difficulty codenames [beginner, challenging, improbable]. */
  diffCodes: string[] | null;
  /** Unique operative codename claimed from ai.missioncodenames — the name
   *  that goes in the acceptance SMS. Stable for the run once claimed. */
  codeName: string | null;
  /** The case they unlocked on the desk, open on the Case step until they
   *  accept the mission in it (or pick another). */
  caseTier: Tier | null;
  /** Tier whose envelope was tapped — the tap IS the acceptance. */
  tier: Tier | null;
  /** glow.missionbank row accepted with that tier. Kept, not re-derived: the
   *  doors rotate daily, so a debrief filed tomorrow would resolve another. */
  missionId: number | null;
  /** When that mission was accepted (ms epoch) — the mission-live screen's
   *  24h window counts down from here. */
  acceptedAt: number | null;
  /** When the clock started on the on-the-clock screen, before the accept
   *  is sent: the accept takes it as acceptedAt, so the live mission picks
   *  up the same countdown. */
  clockFrom: number | null;
  /** Reached the delivered screen. */
  requested: boolean;
  /** Display name of the member whose KEY QR brought this visitor here,
   *  resolved from the scanned code. Fills {referrer} in the intro copy. */
  referrerName: string | null;
  /** When they asked for their next mission's text (ms epoch), picked on the
   *  well-done screen. null = the step's own timing (tomorrow). */
  nextMissionAt: number | null;
  /** The mission just reported (1 = their first), fixed at the moment they
   *  submit — the same lock the vault opened. Filing reloads their file, so
   *  the debrief must not re-count it (nor guess when the file can't load). */
  filedMissionNo: number | null;
  /** Engraved the key on this run's first-mission debrief: a reload there
   *  goes on to well done rather than burning the name in again. */
  keyEngraved: boolean;
  /** Tomorrow's case, sealed before they left (the Comeback Loop): the door
   *  they chose for it, its title, and when it opens (ms epoch). null until
   *  they seal one; cleared when the next mission is accepted. */
  sealed: { door: Door; label: string; at: number } | null;
}

interface FunnelActions {
  answerFeeling: () => void;
  acceptOrder: () => void;
  pickDoor: (pick: { door: Door; opCode: string }) => void;
  pickHandler: (handler: string) => void;
  setDiffCodes: (codes: string[]) => void;
  setCodeName: (codeName: string) => void;
  setReferrerName: (name: string) => void;
  openCase: (tier: Tier) => void;
  acceptMission: (accept: { tier: Tier; missionId?: number | null }) => void;
  setNextMissionAt: (at: number | null) => void;
  setFiledMissionNo: (n: number) => void;
  setKeyEngraved: (engraved: boolean) => void;
  sealTomorrow: (sealed: { door: Door; label: string; at: number }) => void;
  resetFunnel: () => void;
}

export const emptyFunnelState: FunnelState = {
  handlerPromotionComplete: false,
  feelingAnswered: false,
  orderAccepted: false,
  door: null,
  opCode: null,
  handler: null,
  diffCodes: null,
  codeName: null,
  caseTier: null,
  tier: null,
  missionId: null,
  acceptedAt: null,
  clockFrom: null,
  requested: false,
  referrerName: null,
  nextMissionAt: null,
  filedMissionNo: null,
  keyEngraved: false,
  sealed: null,
};

export const useFunnelStore = create<FunnelState & FunnelActions>()(
  persist(
    (set) => ({
      ...emptyFunnelState,
      answerFeeling: () => set({ feelingAnswered: true }),
      acceptOrder: () => set({ orderAccepted: true }),
      // Keep the first-generated code for the run — re-picking a door on a
      // back-navigation must not re-roll the operation code.
      pickDoor: ({ door, opCode }) =>
        set((s) => ({ door, opCode: s.opCode ?? opCode })),
      pickHandler: (handler) => set({ handler }),
      // Lazily generated on first visit to the difficulty screen; stable
      // for the rest of the run.
      setDiffCodes: (codes) =>
        set((s) => ({ diffCodes: s.diffCodes ?? codes })),
      // Claimed once per run; a re-pick / back-nav must not re-roll it.
      setCodeName: (codeName) =>
        set((s) => ({ codeName: s.codeName ?? codeName })),
      setReferrerName: (referrerName) => set({ referrerName }),
      openCase: (caseTier) => set({ caseTier }),
      acceptMission: ({ tier, missionId = null }) =>
        set((s) => ({
          tier,
          missionId,
          acceptedAt: s.clockFrom ?? Date.now(),
          clockFrom: null,
          requested: true,
          // a new mission: nothing reported on it yet
          filedMissionNo: null,
          keyEngraved: false,
          // the sealed case was for this mission: opened now
          sealed: null,
        })),
      setNextMissionAt: (nextMissionAt) => set({ nextMissionAt }),
      setFiledMissionNo: (filedMissionNo) => set({ filedMissionNo }),
      setKeyEngraved: (keyEngraved) => set({ keyEngraved }),
      sealTomorrow: (sealed) => set({ sealed }),
      resetFunnel: () => set(emptyFunnelState),
    }),
    {
      name: STORAGE_KEY,
      version: 1,
      // Persist data fields only, never the action functions.
      partialize: (s): FunnelState => ({
        handlerPromotionComplete: s.handlerPromotionComplete ?? false,
        feelingAnswered: s.feelingAnswered,
        orderAccepted: s.orderAccepted,
        door: s.door,
        opCode: s.opCode,
        handler: s.handler,
        diffCodes: s.diffCodes,
        codeName: s.codeName,
        caseTier: s.caseTier,
        tier: s.tier,
        missionId: s.missionId,
        acceptedAt: s.acceptedAt,
        clockFrom: s.clockFrom ?? null,
        requested: s.requested,
        referrerName: s.referrerName,
        nextMissionAt: s.nextMissionAt ?? null,
        filedMissionNo: s.filedMissionNo ?? null,
        keyEngraved: s.keyEngraved ?? false,
        sealed: s.sealed ?? null,
      }),
      // Unknown/legacy payloads (including the pre-Zustand raw shape)
      // reset to a fresh run instead of hydrating garbage.
      migrate: (persisted) => (persisted as FunnelState) ?? emptyFunnelState,
    },
  ),
);
